import { useMemo } from 'react';

import {
  Box,
  CardArticle,
  CardMedia,
  Grid,
  LinkTranslatedStyledAsLink,
  StyledCardMediaWrapper,
} from '@core/component';
import { useContextMediaQuery, useContextProfile, useContextUser } from '@core/context';
import { urlFor } from '@core/sanity';
import { EActionAttribute, EColor, ERouting } from '@core/type';
import { SanityBlogHome } from '@core/type/sanity';
import { MiniBannerSpotlight } from '@core/widget';

export const BlogHomeMainArticle = ({
  mainArticle: {
    title,
    subtitle,
    body: { image, ...body },
    pageSlug: { slug },
  },
  isSubscriptionSuccessful,
}: {
  mainArticle: SanityBlogHome['mainArticle'];
  isSubscriptionSuccessful: boolean;
}) => {
  const { isPhone, isDesktop } = useContextMediaQuery();

  const { hasNewsletterSubscription } = useContextProfile();
  const { isLogged } = useContextUser();
  const hasSubscription = (isLogged && hasNewsletterSubscription) || isSubscriptionSuccessful;
  const showSpotlightMiniBanner = isDesktop && !hasSubscription;

  const bannerWidth = { lg: 3, xl: 2 };
  const bodyWidth = useMemo(() => {
    const width = 6;
    const offset = showSpotlightMiniBanner && bannerWidth;
    return {
      lg: width - (offset?.lg || 0),
      xl: width - (offset?.xl || 0),
    };
  }, [showSpotlightMiniBanner]);

  return (
    <Box marginBottom={'64px'}>
      <Grid isContainer columnSpacing={30}>
        <Grid xs={12} lg={6} xl={6}>
          <StyledCardMediaWrapper headerColor={EColor.PRIMARY}>
            <LinkTranslatedStyledAsLink
              keyRoute={ERouting.BLOG_ARTICLE}
              query={{
                category: body.category?.pageSlug?.slug?.current,
                slug: slug.current,
              }}
            >
              <CardMedia
                height={isPhone ? '208px' : '324px'}
                image={{ ...image, src: urlFor(image) }}
                withVideo={body?.hasVideo}
              />
            </LinkTranslatedStyledAsLink>
          </StyledCardMediaWrapper>
        </Grid>
        <Grid xs={12} {...bodyWidth}>
          <Box marginTop={isDesktop ? '0' : '32px'}>
            <CardArticle
              {...body}
              title={title}
              subtitle={subtitle}
              headerColor={EColor.PRIMARY}
              categoryColor={EColor.ACCENT}
              actionAttribute={EActionAttribute.H2}
              iconColor={EColor.ACCENT}
              linkTranslatedProps={{
                keyRoute: ERouting.BLOG_ARTICLE,
                query: {
                  category: body.category?.pageSlug?.slug?.current,
                  slug: slug.current,
                },
              }}
              showImage={false}
            />
          </Box>
        </Grid>
        {showSpotlightMiniBanner && (
          <Grid {...bannerWidth}>
            <MiniBannerSpotlight />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
